import React, { useCallback, useState } from "react";
import { Autocomplete, Circle, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Config from "../Helpers/Config";

// Deep-dive report §2/§10#2-3: the Register page used to be a free-text
// address field plus a hand-typed distance number, with nothing checking
// either was real - unlike the legacy map picker, which showed a live
// "Distance from hub" reading, drew a red circle past the max radius, and
// disabled Confirm while out of range. This restores that UX, reusing the
// same Google Maps JS SDK + REACT_APP_MAP_KEY the Hub form's LocationPicker
// already uses (see Components/LocationPicker.js).
//
// The distance shown here is an in-browser straight-line estimate
// (google.maps.geometry.spherical.computeDistanceBetween) - cheap, free, and
// good enough to steer the admin away from an obviously-out-of-range point
// live, without billing Google's Distance Matrix API on every pixel of a
// drag. The number that actually prices the booking and gets stored is
// measured server-side, once, via the real (driving) Distance Matrix API -
// see BookingUtils.resolveVerifiedDistance, called from both
// BookingController.quote and BookingController.prebook. A driving distance
// is normally >= the straight-line one, so this client estimate is treated
// as a lower bound / early warning, never the authoritative figure.
//
// Fully controlled, same contract as LocationPicker: the parent owns
// latitude/longitude/address and gets every change back through onChange -
// this component keeps no location state of its own.

const containerStyle = { width: "100%", height: "280px", borderRadius: "12px" };
const DEFAULT_ZOOM = 13;
const PICKED_ZOOM = 14;

const metersToKm = (meters) => Math.round((meters / 1000) * 10) / 10;

const DeliveryLocationPicker = ({
    hubLat, hubLng, hubLabel,
    latitude, longitude, address,
    maxKm,
    onChange,
    // Manual fallback, used only when Maps can't load (missing key / load
    // error) - mirrors the plain text+number inputs this component replaces.
    manualDistanceKm,
    onManualChange,
}) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: Config.KEYS.MAP_KEY || "",
        libraries: Config.GOOGLE_MAPS_LIBRARIES,
    });
    const [autocomplete, setAutocomplete] = useState(null);
    const [geocoder, setGeocoder] = useState(null);

    const hasHub = Number.isFinite(Number(hubLat)) && Number.isFinite(Number(hubLng));
    const hub = hasHub ? { lat: Number(hubLat), lng: Number(hubLng) } : null;
    const hasPoint = latitude !== "" && latitude !== undefined && latitude !== null
        && longitude !== "" && longitude !== undefined && longitude !== null
        && !Number.isNaN(Number(latitude)) && !Number.isNaN(Number(longitude));
    const point = hasPoint ? { lat: Number(latitude), lng: Number(longitude) } : null;
    const center = point || hub || { lat: 22.3511, lng: 78.6677 };

    // Computed fresh for whatever point is being emitted (a click/drag/search
    // result), never from the `point` prop above - that still reflects the
    // PREVIOUS render, so using it here would show the old distance for one
    // render after every change.
    const computeKm = useCallback((lat, lng) => {
        if (!hasHub || !window.google?.maps?.geometry) return null;
        return metersToKm(window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(Number(hubLat), Number(hubLng)),
            new window.google.maps.LatLng(lat, lng)
        ));
    }, [hasHub, hubLat, hubLng]);

    const distanceKm = point ? computeKm(point.lat, point.lng) : null;
    const withinLimit = !maxKm || distanceKm === null || distanceKm <= Number(maxKm);

    const emitChange = useCallback((lat, lng, resolvedAddress) => {
        const km = computeKm(lat, lng);
        onChange?.({ lat, lng, address: resolvedAddress, distanceKm: km, withinLimit: !maxKm || km === null || km <= Number(maxKm) });
    }, [computeKm, onChange, maxKm]);

    const reverseGeocode = useCallback((latLng, fallbackAddress) => {
        const lat = latLng.lat();
        const lng = latLng.lng();
        if (!geocoder) {
            emitChange(lat, lng, fallbackAddress);
            return;
        }
        geocoder.geocode({ location: latLng }, (results, status) => {
            const resolved = status === "OK" && results?.[0]?.formatted_address ? results[0].formatted_address : fallbackAddress;
            emitChange(lat, lng, resolved);
        });
    }, [geocoder, emitChange]);

    const handleMapLoad = () => {
        if (window.google?.maps && !geocoder) setGeocoder(new window.google.maps.Geocoder());
    };
    const handleMapClick = (event) => reverseGeocode(event.latLng, address);
    const handleMarkerDragEnd = (event) => reverseGeocode(event.latLng, address);
    const handlePlaceChanged = () => {
        const place = autocomplete?.getPlace();
        if (!place?.geometry?.location) return;
        emitChange(place.geometry.location.lat(), place.geometry.location.lng(), place.formatted_address || address);
    };

    if (!Config.KEYS.MAP_KEY || loadError) {
        return (
            <div className="delivery-location-picker-fallback">
                <div className="form-text mb-2">
                    {!Config.KEYS.MAP_KEY ? "Map isn't configured (missing REACT_APP_MAP_KEY)" : "Couldn't load Google Maps"} - enter the address and distance manually.
                </div>
                <input
                    type="text" className="form-control form-control-sm mb-2" placeholder="Address"
                    value={address || ""} onChange={(e) => onManualChange?.({ address: e.target.value })}
                />
                <input
                    type="number" min="0" step="0.1" className="form-control form-control-sm" placeholder="Distance from hub (km)"
                    value={manualDistanceKm ?? ""} onChange={(e) => onManualChange?.({ distanceKm: e.target.value })}
                />
            </div>
        );
    }

    if (!isLoaded) {
        return <div className="delivery-location-picker-fallback">Loading map...</div>;
    }

    return (
        <div className="delivery-location-picker">
            <Autocomplete onLoad={setAutocomplete} onPlaceChanged={handlePlaceChanged}>
                <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Search for the delivery/pickup address..."
                    defaultValue={address || ""}
                />
            </Autocomplete>

            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={hasPoint ? PICKED_ZOOM : DEFAULT_ZOOM}
                onClick={handleMapClick}
                onLoad={handleMapLoad}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
            >
                {hub ? <Marker position={hub} label={{ text: "H", color: "#fff" }} title={hubLabel || "Hub"} /> : null}
                {hub && maxKm ? (
                    <Circle
                        center={hub}
                        radius={Number(maxKm) * 1000}
                        options={{
                            strokeColor: withinLimit ? "#198754" : "#dc3545",
                            strokeOpacity: 0.6,
                            strokeWeight: 1,
                            fillColor: withinLimit ? "#198754" : "#dc3545",
                            fillOpacity: 0.08,
                            clickable: false,
                        }}
                    />
                ) : null}
                {point ? <Marker position={point} draggable onDragEnd={handleMarkerDragEnd} /> : null}
            </GoogleMap>

            <div className={`form-text mt-1 ${withinLimit ? "" : "text-danger fw-semibold"}`}>
                {point
                    ? (distanceKm !== null
                        ? `Distance from hub (approx): ${distanceKm} km${maxKm ? ` of ${maxKm} km max` : ""}${withinLimit ? "" : " - outside the deliverable radius"}`
                        : "Search an address, or click the map, to place a pin.")
                    : "Search an address, or click the map, to place a pin."}
            </div>
            <div className="form-text">The exact distance used for pricing is re-measured on the server when you get a quote.</div>
        </div>
    );
};

export default DeliveryLocationPicker;
